import { Account, RpcProvider } from "starknet";

import { KeybanClientBase, KeybanClientConfig, MetadataConfig } from "~/client";
import { Hex } from "~/index";
import { StarknetAccount } from "~/starknet/account";
import { StarknetSigner } from "~/starknet/signer";

export class StarknetClient extends KeybanClientBase {
  #rpcProvider: Promise<RpcProvider>;

  constructor(
    config: KeybanClientConfig,
    metadataConfig?: Promise<MetadataConfig>,
  ) {
    super(config, metadataConfig);

    this.#rpcProvider = this.metadataConfig.then(
      (config) => new RpcProvider({ nodeUrl: config.network.rpcUrl }),
    );
  }

  /**
   * Initialize the Starknet account for the configured network.
   * Provisions an ECDSA client share for Cairo-compatible signing.
   * @returns A ready-to-use {@link StarknetAccount}.
   * @example
   * const account = await starknetClient.initialize();
   * console.log(account.address);
   */
  async initialize(): Promise<StarknetAccount> {
    const key = `ecdsa:${this.network}`;

    let clientShare = await this.clientShareProvider.get(key);
    if (!clientShare) {
      clientShare = await this.api.ecdsa.dkg();
      await this.clientShareProvider.set(key, clientShare);
    }

    const [provider, publicKey, address] = await Promise.all([
      this.#rpcProvider,
      this.api.ecdsa
        .publicKey(clientShare)
        // remove the 04 prefix from the public key (it's the ECDSA uncompressed key prefix)
        .then((ethPublicKey): Hex => `0x${ethPublicKey.slice(4)}`),
      this.api.account.getAddress(),
    ]);

    const signer = new StarknetSigner(this.api, clientShare, publicKey);

    const account = new Account(provider, address, signer);

    return new StarknetAccount(this.api, account, publicKey);
  }
}
